import React from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, Table } from "@mantine/core";

import { IconClipboardHeart } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import NewbornApgarRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornApgarRow";
import NewbornAssessmentNoteRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornAssessmentNoteRow";
import NewbornCordClampedRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornCordClampedRow";
import NewbornFeedingRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornFeedingRow";
import NewbornFirstLatchRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornFirstLatchRow";
import NewbornInitialAssessmentRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornInitialAssessmentRow";
import NewbornLocationRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornLocationRow";
import NewbornMedsAndTherapyRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornMedsAndTherapyRow";
import NewbornVitalsRow from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/TableRows/NewbornVitalsRow";
import { PostpartumTableRows } from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTableRows";
import DateRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/DateRow";
import AdmissionDischargeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/AdmissionDischargeRow";
import BirthDateTimeRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthDateTimeRow";
import BirthingRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/BirthingRow";
import ShoulderDystociaRow from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/LaborObservationRow/ShoulderDystociaRow";
import { usePostpartumRows } from "@/encounters/components/EncounterLabor/hooks/usePostpartumRows";
import { PostpartumRow } from "@/encounters/components/EncounterLabor/types/postpartum";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";

const ROW_COMPONENT_BY_FORM = {
  [NewbornPostpartumFlowsheetForms.NewbornBirthing]: BirthingRow,
  [NewbornPostpartumFlowsheetForms.NewbornShoulderDystocia]: ShoulderDystociaRow,
  [NewbornPostpartumFlowsheetForms.NewbornAssessmentNote]: NewbornAssessmentNoteRow,
  [NewbornPostpartumFlowsheetForms.NewbornCordCutClamped]: NewbornCordClampedRow,
  [NewbornPostpartumFlowsheetForms.NewbornFirstLatch]: NewbornFirstLatchRow,
  [NewbornPostpartumFlowsheetForms.NewbornFeeding]: NewbornFeedingRow,
  [NewbornPostpartumFlowsheetForms.NewbornVitals]: NewbornVitalsRow,
  [NewbornPostpartumFlowsheetForms.NewbornMedsTherapies]: NewbornMedsAndTherapyRow,
  [NewbornPostpartumFlowsheetForms.NewbornLocation]: NewbornLocationRow,
  [NewbornPostpartumFlowsheetForms.NewbornInitialAssessment]: NewbornInitialAssessmentRow,
  [NewbornPostpartumFlowsheetForms.NewbornApgar]: NewbornApgarRow,
  [NewbornPostpartumFlowsheetForms.NewbornAdmissionDischarge]: AdmissionDischargeRow,
  // Add cross-flowsheet mappings for shared forms (Cord Cut/Clamped and First Latch)
  [ClientPostpartumFlowsheetForms.ClientCordCutClamped]: NewbornCordClampedRow,
  [ClientPostpartumFlowsheetForms.ClientFirstLatch]: NewbornFirstLatchRow,
  [QuiltedHealthCodes.BirthDateTime]: BirthDateTimeRow,
};

interface NewbornTableRowsProps {
  showNumbersOnly: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

export default function NewbornTableRows({
  showNumbersOnly,
  onRefresh,
  isViewOnly,
}: NewbornTableRowsProps) {
  const { rows, error, refresh } = usePostpartumRows(NewbornPostpartumFlowsheetForms);

  // If there's an error, we'll still show the existing rows but log the error
  if (error) {
    console.error("Error in NewbornTableRows:", error);
  }

  // Combine the refresh functions
  const handleRefresh = async () => {
    await refresh();
    if (onRefresh) {
      await onRefresh();
    }
  };

  // Filter rows to only include those with observations
  const rowsWithObservations = rows.filter(([_, groups]) =>
    (groups as { observations: Observation[] }[]).some((group) => group.observations.length > 0),
  );

  return (
    <>
      {rowsWithObservations.length > 0 ? (
        rowsWithObservations.map(([date, groups]: [string, PostpartumRow[]]) => (
          <React.Fragment key={date}>
            <DateRow date={date} />
            {groups.map((group) => (
              <PostpartumTableRows
                key={group.time}
                {...group}
                isViewOnly={isViewOnly}
                onRefresh={handleRefresh}
                flowsheetId="newbornPostpartum"
                showNumbersOnly={showNumbersOnly}
                rowComponentByForm={ROW_COMPONENT_BY_FORM}
                flowsheetForms={NewbornPostpartumFlowsheetForms}
              />
            ))}
          </React.Fragment>
        ))
      ) : (
        <Table.Tr>
          <Table.Td p="md" bg="white" ta="center" colSpan={10}>
            <Group
              p="xs"
              gap="xs"
              w="100%"
              bg="gray.0"
              display="flex"
              justify="center"
              style={{ borderRadius: "var(--mantine-radius-xs)" }}
            >
              <IconClipboardHeart size={18} color="var(--mantine-color-gray-6)" />
              <TextWrapper fz="md" c="gray.7" variant="default">
                No newborn postpartum data available
              </TextWrapper>
            </Group>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
